import { isSameDay, differenceInDays, parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

export const getFormattedDateRangeWithMultiDaySupport = ({
  startingAt,
  timeZone,
  endingAt,
  withTime,
}: {
  startingAt?: string | null;
  timeZone?: string | null;
  endingAt?: string | null;
  withTime?: boolean;
}) => {
  if (!startingAt || !timeZone) {
    return {
      datePart: '',
      utcPart: '',
      isMultiDay: false,
      duration: 0,
    };
  }

  const startDate = parseISO(startingAt);
  const endDate = endingAt ? parseISO(endingAt) : null;
  const isMultiDay = Boolean(endDate && !isSameDay(startDate, endDate));
  const duration = endDate ? differenceInDays(endDate, startDate) + 1 : 1;

  let startingAtString: string;
  let endingAtString: string;

  // Date/time format strings using date-fns v2 tokens
  const dateFormat = 'EEE MMMM dd, yyyy';
  const dateTimeFormat = 'EEE MMMM dd, yyyy, h:mm a';
  const timeFormat = 'h:mm a';

  if (isMultiDay) {
    // Multi-day event: show full dates for both start and end
    startingAtString = formatInTimeZone(
      startingAt,
      timeZone,
      withTime ? dateTimeFormat : dateFormat
    );
    endingAtString = endingAt
      ? formatInTimeZone(endingAt, timeZone, withTime ? dateTimeFormat : dateFormat)
      : 'Ongoing';
  } else {
    // Single-day event: show date once, then times
    const dateString = formatInTimeZone(startingAt, timeZone, dateFormat);

    if (withTime && endingAt) {
      const startTime = formatInTimeZone(startingAt, timeZone, timeFormat);
      const endTime = formatInTimeZone(endingAt, timeZone, timeFormat);
      startingAtString = `${dateString}, ${startTime}`;
      endingAtString = endTime;
    } else if (withTime) {
      const startTime = formatInTimeZone(startingAt, timeZone, timeFormat);
      startingAtString = `${dateString}, ${startTime}`;
      endingAtString = 'Ongoing';
    } else {
      startingAtString = dateString;
      endingAtString = endingAt ? formatInTimeZone(endingAt, timeZone, dateFormat) : 'Ongoing';
    }
  }

  const sanitizedTimeZone = timeZone.replace('_', ' ');
  const utc = `UTC${formatInTimeZone(startingAt, timeZone, 'xxx')}`;
  const utcPart = `(${utc}) ${sanitizedTimeZone}`;

  const datePart = endingAt
    ? `${startingAtString} till ${endingAtString}`
    : `${startingAtString} - ${endingAtString}`;

  return {
    datePart,
    utcPart,
    isMultiDay,
    duration,
  };
};
